<template>
  <div class="cursor-pointer" @click="$emit('toggle-expanded')">
    <div class="flex items-start sm:items-center gap-2 sm:gap-3">
      <ChevronDownIcon
        class="h-5 w-5 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 mt-0.5 sm:mt-0"
        :class="{ 'rotate-180': !isExpanded }"
      />
      <div class="flex-1 min-w-0">
        <div class="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-1 sm:gap-2">
          <span
            class="font-medium text-gray-900 dark:text-white truncate inline-flex items-center gap-2.5"
          >
            {{ preference.role || 'Geen rol opgegeven' }}
            <span class="inline-flex items-center gap-1.5">
              <button
                @click.stop="$emit('start-editing')"
                class="text-amber-400 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300"
                title="Rol bewerken"
              >
                <PencilIcon class="h-3.5 w-3.5" />
              </button>
              <button
                @click.stop="$emit('confirm-delete')"
                class="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                title="Rol verwijderen"
              >
                <XMarkIcon class="h-4.5 w-4.5" />
              </button>
            </span>
          </span>
          <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400"
            >{{ preference.dpw }} dagen/week</span
          >
          <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <CalendarIcon class="h-3 sm:h-3.5 w-3 sm:w-3.5 flex-shrink-0" />
            <span v-if="preference.start_date && preference.end_date" class="truncate">
              {{ formatDate(preference.start_date) }} - {{ formatDate(preference.end_date) }}
            </span>
            <span v-else class="text-gray-400 dark:text-gray-500 truncate">
              {{ formatDate(prospect?.start_date) }} -
              {{ formatDate(prospect?.expected_closing_date) }}
              <span class="text-xs">(project)</span>
            </span>
          </span>
          <!-- Ervaringsniveau -->
          <span
            v-if="experienceLevels.length > 0"
            class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1"
          >
            <span class="truncate">Niveau: {{ experienceLevels.join(', ') }}</span>
          </span>
        </div>
        <!-- Selection badge - on mobile show below, on desktop show inline -->
        <div class="mt-2 sm:hidden">
          <span
            v-if="selection"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
          >
            {{ selectionName }}
          </span>
          <span
            v-else
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300"
          >
            Geen selectie
          </span>
        </div>
      </div>
      <!-- Desktop selection badge -->
      <div class="hidden sm:flex items-center gap-2 flex-shrink-0">
        <span
          v-if="selection"
          class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
        >
          {{ selectionName }}
        </span>
        <span
          v-else
          class="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300"
        >
          Geen selectie
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { XMarkIcon, ChevronDownIcon, CalendarIcon, PencilIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  preference: {
    type: Object,
    required: true
  },
  prospect: {
    type: Object,
    default: null
  },
  selection: {
    type: Object,
    default: null
  },
  selectionName: {
    type: String,
    default: ''
  },
  preferredTeamNames: {
    type: Array,
    default: () => []
  },
  isExpanded: {
    type: Boolean,
    default: true
  }
})

const experienceLevels = computed(() => {
  const levels = props.preference.experience_levels
  if (!levels) return []
  return Array.isArray(levels) ? levels : JSON.parse(levels || '[]')
})

defineEmits(['toggle-expanded', 'start-editing', 'confirm-delete'])

const formatDate = dateString => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}
</script>
